import { useEffect, useRef } from 'react';

/**
 * O CAMPO DE OPÇÃO ÚNICA — a regra geral (pedido do dono, 27/10/2026).
 *
 * *"Se tiver liberado um tipo só ou um campo só, no geral, nem abre o
 * dropdown: já mostra o campo autopreenchido e desabilitado para edição."*
 *
 * Um campo de escolha OBRIGATÓRIO com EXATAMENTE UMA opção não é pergunta: a
 * resposta já se sabe. Abrir uma lista para escolher o único item é um toque a
 * troco de nada — e, no campo, com luva e sol na tela, é um toque a mais em
 * cada lançamento. O campo mostra o valor, travado, com a ajuda do porquê, e o
 * valor vai no envio.
 *
 * Vale por qualquer motivo que deixe uma opção só: a trava do papel (um tanque
 * liberado), o filtro de outro campo (o produto que só um tanque tem), ou o
 * cadastro que só tem um item. Quando a lista ainda carrega, o campo é
 * esqueleto (quem decide é o componente); quando chega com uma só, preenche e
 * trava; se o campo de quem ele depende muda e voltam a ser várias, volta a ser
 * lista.
 *
 * NÃO vale para o campo opcional (a opção vazia é uma resposta: "Sem ordem"),
 * nem para o filtro ("Todas"): ali escolher a única opção mudaria o que a
 * pessoa vê sem ela ter pedido.
 *
 * A regra mora AQUI, e os componentes de escolha da mesa (`ListaDeEscolha`,
 * `CampoSelecao`) e do celular (`Selecao`) a leem. Copiada em cada formulário,
 * a primeira mudança deixaria metade das telas abrindo lista de um item só.
 */

/** A ajuda padrão do campo travado: frase completa, com ponto (p.23). */
export const AJUDA_DA_OPCAO_UNICA = 'Única opção.';

export interface EntradaDaEscolhaUnica {
  /** Os valores das opções que o campo oferece agora (sem a opção vazia). */
  readonly opcoes: readonly string[];
  /** O campo exige resposta: a opção vazia não vale. */
  readonly obrigatoria: boolean;
  /** A lista ainda não chegou: não se decide nada sobre ela. */
  readonly carregando?: boolean;
}

/** O valor único que o campo assume, ou `null` quando ele continua sendo lista. */
export function opcaoUnica(entrada: EntradaDaEscolhaUnica): string | null {
  if (!entrada.obrigatoria || entrada.carregando === true) return null;
  const distintas = [...new Set(entrada.opcoes)];
  return distintas.length === 1 ? (distintas[0] ?? null) : null;
}

/**
 * O campo da MESA é obrigatório? O `ListaDeEscolha` não tem a palavra
 * "obrigatório": ele tem a opção vazia, e o texto dela diz o que ela é.
 *
 *   - "Escolha a máquina", "Escolha o tanque": CONVITE — a opção vazia não é
 *     resposta, o campo é obrigatório;
 *   - "Todas", "Sem contrato", "Nenhuma", "Herda da configuração": RESPOSTA —
 *     o vazio vale, e o campo continua lista;
 *   - sem opção vazia: só as opções valem, o campo é obrigatório.
 *
 * `opcional` e `aplicado` (o chip de filtro) mandam antes do texto.
 */
export function obrigatoriaNaMesa(campo: {
  readonly opcional?: boolean;
  readonly aplicado?: boolean;
  readonly rotuloVazio?: string | undefined;
}): boolean {
  if (campo.opcional === true || campo.aplicado === true) return false;
  return campo.rotuloVazio === undefined || /^escolha\b/i.test(campo.rotuloVazio.trim());
}

/**
 * O gancho dos componentes: devolve o valor único (ou `null`) e PREENCHE o
 * campo com ele — o valor que a pessoa não escolheu tem de ir no envio do mesmo
 * jeito. Preenche só quando o valor de agora não é o único: a escolha que já
 * está certa não dispara nada, e a cascata do formulário (a máquina que
 * escolhe a obra) roda uma vez.
 */
export function useOpcaoUnica(entrada: EntradaDaEscolhaUnica & { readonly valor: string; readonly aoMudar: (valor: string) => void }): string | null {
  const unica = opcaoUnica(entrada);
  const { valor, aoMudar } = entrada;
  /* O QUE JÁ FOI PEDIDO, para não insistir: o formulário que recebe o valor e
     não o guarda (ou o guarda noutra forma) não pode virar um laço de
     pedidos. Pede de novo só quando algo mudou — o valor (o formulário zerou
     depois de lançar) ou a opção única (outro campo mudou a lista). */
  const visto = useRef<VistoDaOpcaoUnica | null>(null);
  useEffect(() => {
    const antes = visto.current;
    visto.current = { valor, unica };
    if (devePreencher(antes, valor, unica)) aoMudar(unica as string);
  }, [unica, valor, aoMudar]);
  return unica;
}

/** O que o gancho viu da última vez: o valor do campo e a opção única. */
export interface VistoDaOpcaoUnica {
  readonly valor: string;
  readonly unica: string | null;
}

/**
 * Preenche agora? Só quando há opção única, o campo não está nela, e algo
 * mudou desde o último pedido — sem o último, o formulário que não guarda o
 * valor viraria um laço de pedidos. Pura, para o teste provar a regra sem
 * desenhar a tela.
 */
export function devePreencher(antes: VistoDaOpcaoUnica | null, valor: string, unica: string | null): boolean {
  if (unica === null || valor === unica) return false;
  return antes === null || antes.valor !== valor || antes.unica !== unica;
}
